import "./ImagePreview.css";
import { Row, Col, Button, Input, Modal, Select, Space, message, Spin } from "antd";
import {
  FullscreenOutlined,
  DownloadOutlined,
  EditOutlined,
} from "@ant-design/icons";
import { useMemo, useRef, useState, useEffect } from "react";
import { AttachSignedUrls } from "../../../../utils/GetSignedURL";
import { DownloadAsZip } from "../../../../utils/DownloadAsZip";

const ImagePreview = ({ generatedImages = [], setGeneratedImages, sessionId, title="Generated Image", onReviseDescription, onRegenerate }) => {
  const imgBoxRef = useRef(null);

  const [messageApi, contextHolder] = message.useMessage();

  const [open, setOpen] = useState(false);
  const [reviseText, setReviseText] = useState("");
  const [selectedKey, setSelectedKey] = useState("0");

  const [signing, setSigning] = useState(false);

  const [revisedDescByKey, setRevisedDescByKey] = useState({});
  const [reviseLoadingKey, setReviseLoadingKey] = useState(null);
  const [regenLoadingKey, setRegenLoadingKey] = useState(null);

  const [isEditingDesc, setIsEditingDesc] = useState(false);
  const [editDescText, setEditDescText] = useState("");
  const [isHoveringDesc, setIsHoveringDesc] = useState(false);
  const [isSavingDesc, setIsSavingDesc] = useState(false);

  const total = generatedImages.length;
  const completed = generatedImages.filter((x) => !x?.generating).length;
  const generating = generatedImages.some((x) => x?.generating);

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      if (!sessionId || !(generatedImages ?? []).length) return;

      const imagesToSign = generatedImages.filter(
        (x) => x?.imagePath && !x?.signed_url
      );

      if (!imagesToSign.length) return;

      try {
        setSigning(true);

        const withUrl = await AttachSignedUrls(imagesToSign, 1);

        if (cancelled) return;

        const signedMap = new Map(withUrl.map((x) => [x.id, x.signed_url]));

        const nextImages = generatedImages.map((img) => {
          const signedUrl = img?.id ? signedMap.get(img.id) : null;
          return signedUrl ? { ...img, signed_url: signedUrl } : img;
        });

        setGeneratedImages(nextImages);

      } catch (e) {
        console.error(e);
        messageApi.error("Failed to sign image URLs");
      } finally {
        if (!cancelled) setSigning(false);
      }
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [generatedImages, sessionId]);

  useEffect(() => {
    setIsEditingDesc(false);
    setEditDescText("");
    setIsHoveringDesc(false);
  }, [selectedKey]);

  const items = useMemo(() => {
    return (generatedImages ?? []).map((it, idx) => {
      const signedUrl =
        it?.signed_url?.signed_url ??
        it?.signed_url ??
        "";

      return {
        key: String(idx),
        signedUrl,
        filename: `generated_${idx}.jpg`,
        description: it?.prompt ?? "",
        promptId: it?.promptId ?? "",
        campaignImageId: it?.id ?? "",
        raw: it,
      };
    });
  }, [generatedImages]);

  useEffect(() => {
    if (!items.length) return;

    const latestReady = [...items].reverse().find((x) => x.signedUrl);

    if (latestReady) {
      setSelectedKey(latestReady.key);
    }
  }, [items]);

  useEffect(() => {
    if (!items.length) return;
    if (!items.some((x) => x.key === selectedKey)) setSelectedKey(items[0].key);
  }, [items, selectedKey]);

  const current = items.find((x) => x.key === selectedKey) || items[0];
  const currentUrl = current?.signedUrl || "";
  const currentDesc = revisedDescByKey[selectedKey] ?? (current?.description || "");
  
  const hasPendingRevision = revisedDescByKey[selectedKey] != null;
  const hasFailed = !!current?.raw?.error;
  const canRegenerate = hasPendingRevision || hasFailed;

  const forceDownload = async (url, filename) => {
    const res = await fetch(url, { method: "GET" });
    if (!res.ok) throw new Error(`Download failed: ${res.status}`);

    const blob = await res.blob();
    const blobUrl = URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();

    URL.revokeObjectURL(blobUrl);
  }

  const handleDownloadCurrent = async () => {
    if (!current?.signedUrl || signing) return;
    await forceDownload(current.signedUrl, current.filename);
  };

  return (
    <div className="ip-grid-container">
      {generating && (
        <div className="ip-globalGenerating">
          <Spin size="small" />
          <span>AI is generating your images... ({completed}/{total})</span>
        </div>
      )}
      {!generating && (generatedImages ?? []).some((x) => x?.error) && (
        <div className="ip-globalGenerating">
          <span style={{ color: "red" }}>
            {(generatedImages ?? []).filter((x) => x?.error).length} image(s) failed to generate
          </span>
        </div>
      )}
      {contextHolder}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          gap: 12,
          flexWrap: "wrap",
          marginBottom: 6,
        }}
      >
        <Space wrap>
          <Select
            value={selectedKey}
            onChange={setSelectedKey}
            style={{ width: 220 }}
            disabled={!items.length}
            options={items.map((_, idx) => ({
              value: String(idx),
              label: `Image ${idx + 1}`,
            }))}
          />
          {signing && <span style={{ opacity: 0.7 }}>Loading image…</span>}
        </Space>

        <Space wrap>
          <Button
            icon={<DownloadOutlined />}
            onClick={handleDownloadCurrent}
            disabled={!currentUrl || signing}
          >
            Download Image
          </Button>
          <Button
            icon={<DownloadOutlined />}
            disabled={!items.some((x) => !!x.signedUrl) || signing}
            onClick={async () => {
              try {
                setSigning(true);
                await DownloadAsZip(items, {
                  zipName: `generated_images_${sessionId || "session"}.zip`,
                });
              } catch (e) {
                messageApi.error(e?.message || "Failed to download zip");
              } finally {
                setSigning(false);
              }
            }}
          >
            Download All
          </Button>
        </Space>
      </div>

      {current?.raw?.error && (
        <div style={{ color: "red", fontSize: 12, opacity: 0.8, textAlign: "center" }}>{current.raw.error}</div>
      )}

      <Row gutter={[16, 16]} className="ip-grid">
        <Col xs={24} md={12}>
          <div ref={imgBoxRef} className="ip-mediaBox" onClick={() => currentUrl && setOpen(true)}>
            {current?.raw?.generating ? (
              <div className="ip-generating">
                <Spin />
                <div>Generating image...</div>
              </div>
            ) : currentUrl ? (
              <img className="ip-img" src={currentUrl} alt={title} />
            ) : (
              <div style={{ padding: 24, opacity: 0.7 }}>
                Image URL not available yet
              </div>
            )}

            <Button
              className="ip-fullBtn"
              type="text"
              icon={<FullscreenOutlined />}
              onClick={(e) => {
                e.stopPropagation();
                if (currentUrl) setOpen(true);
              }}
              disabled={!currentUrl}
            />
          </div>

          <Modal
            open={open}
            onCancel={() => setOpen(false)}
            footer={null}
            centered
            width="90vw"
            styles={{ body: { padding: 0 } }}
          >
            <img key={currentUrl} className="ip-modalImg" src={currentUrl} alt={title} />
          </Modal>
        </Col>

        <Col xs={24} md={12}>
          <div className="ip-textPanel">
            <div
              className="ip-textScroll ip-descWrapper"
              onMouseEnter={() => !isEditingDesc && setIsHoveringDesc(true)}
              onMouseLeave={() => setIsHoveringDesc(false)}
            >
              {isEditingDesc ? (
                <Input.TextArea
                  value={editDescText}
                  onChange={(e) => setEditDescText(e.target.value)}
                  autoSize={{ minRows: 4 }}
                  className="ip-descTextarea"
                  autoFocus
                />
              ) : (
                <>
                  <div key={current?.key} className="ip-desc">{currentDesc}</div>
                  {isHoveringDesc && !current?.raw?.generating && (
                    <button
                      className="ip-pencilBtn"
                      onClick={() => {
                        setEditDescText(currentDesc);
                        setIsEditingDesc(true);
                        setIsHoveringDesc(false);
                      }}
                      title="Edit description"
                    >
                      <EditOutlined />
                    </button>
                  )}
                </>
              )}
            </div>

            {isEditingDesc && (
              <div className="ip-editActions">
                <Button
                  size="small"
                  onClick={() => { setIsEditingDesc(false); setEditDescText(""); }}
                  disabled={isSavingDesc}
                >
                  Cancel
                </Button>
                <Button
                  type="primary"
                  size="small"
                  loading={isSavingDesc}
                  disabled={!editDescText.trim() || isSavingDesc}
                  onClick={() => {
                    const trimmed = editDescText.trim();
                    if (!trimmed) return;
                    setRevisedDescByKey((prev) => ({ ...prev, [selectedKey]: trimmed }));
                    setIsEditingDesc(false);
                    setEditDescText("");
                  }}
                >
                  Save
                </Button>
              </div>
            )}

            <div className="ip-reviseRow">
              <Input.TextArea
                value={reviseText}
                onChange={(e) => setReviseText(e.target.value)}
                placeholder="Revise with instructions"
                autoSize={{ minRows: 1, maxRows: 3 }}
                className="ip-reviseTextarea"
                onClick={(e) => e.stopPropagation()}
              />
              
              <Button
                type="primary"
                className="ip-reviseBtn"
                loading={reviseLoadingKey === selectedKey}
                disabled={ !reviseText.trim() || reviseLoadingKey === selectedKey || !current?.promptId || current?.raw?.generating }
                onClick={async () => {
                  const instruction = reviseText.trim();
                  if (!instruction) return;

                  try {
                    setReviseLoadingKey(selectedKey);
                    const newDesc = await onReviseDescription?.(current.promptId, instruction);
                    if (newDesc) {
                      setRevisedDescByKey((prev) => ({ ...prev, [selectedKey]: newDesc }));
                      setReviseText("");
                    }
                  } finally {
                    setReviseLoadingKey(null);
                  }
                }}
              >
                Revise with AI
              </Button>

              {canRegenerate && (
                <Button
                  className="ip-reviseBtn"
                  loading={regenLoadingKey === selectedKey}
                  disabled={regenLoadingKey === selectedKey || current?.raw?.generating}
                  onClick={async () => {
                    try {
                      setRegenLoadingKey(selectedKey);
                      const ok = await onRegenerate?.({
                        indexKey: selectedKey,
                        promptText: revisedDescByKey[selectedKey] ?? currentDesc,
                        promptId: current.promptId,
                      });
                      if (ok) {
                        setRevisedDescByKey((prev) => {
                          const next = { ...prev };
                          delete next[selectedKey];
                          return next;
                        });
                      }
                    } catch (e) {
                    } finally {
                      setRegenLoadingKey(null);
                    }
                  }}
                >
                  Regenerate
                </Button>
              )}

            </div>
          </div>
        </Col>
      </Row>
    </div>
  );
};

export default ImagePreview;
